import React, { useState } from 'react';
import { FormattedMessage } from '../../../../util/reactIntl';
import IconReviewSearchPage from '../ReviewRatings/IconReviewSearchPage/iconReviewSearchPage';

import css from './Reviews.module.css';

const avatarPlaceholder =
  'https://res.cloudinary.com/dlcacck3a/image/upload/v1769082519/socialbnb/assets/icons/avatarPlaceholder.webp';

const googleIcon = 'https://res.cloudinary.com/dlcacck3a/image/upload/v1769082498/socialbnb/assets/google-icon.webp';


export const ReviewCard = ({ review, isGoogleReview, expanded, onExpand, currentLanguage, listingId }) => {
  const maxWordsToShow = 20;
  const [showTranslation, setShowTranslation] = useState(false);

  // Normalize text for comparison
  const normalizeText = text => {
    if (!text) return '';
    return text
      .replace(/\s+/g, ' ') // Replace multiple whitespace with single space
      .trim() // Remove leading/trailing whitespace
      .toLowerCase() // Convert to lowercase for case-insensitive comparison
      .normalize('NFD') // Normalize Unicode characters
      .replace(/[\u0300-\u036f]/g, ''); // Remove diacritics/accents
  };


  // Get the appropriate text based on language and translation state
  const getReviewContent = () => {
    const text = review?.text || '';
    const textEn = review?.text_en || '';
    const textDe = review?.text_de || '';
    const content = review?.attributes?.content || '';

    // Original text is either from Google review (text) or platform review (content)
    const originalText = text || content;

    // If showing translation (user clicked "show original")
    if (showTranslation) {
      return originalText;
    }

    // Based on current language, show translation if available
    if (currentLanguage === 'en' && textEn && textEn !== originalText) {
      return textEn;
    } else if (currentLanguage === 'de' && textDe && textDe !== originalText) {
      return textDe;
    }

    // Default fallback to original
    return originalText;
  };

  // Check if translation button should be shown
  const shouldShowTranslationButton = () => {
    const text = review?.text || '';
    const textEn = review?.text_en || '';
    const textDe = review?.text_de || '';

    // Don't show button if either text_en or text_de is missing
    if (currentLanguage === 'en' && !textEn) return false;
    if (currentLanguage === 'de' && !textDe) return false;

    // Check if texts are similar (first 15 characters)
    const checkSimilarity = (text1, text2) => {
      if (!text1 || !text2) return false;
      const normalized1 = normalizeText(text1.slice(0, 15));
      const normalized2 = normalizeText(text2.slice(0, 15));
      return normalized1 === normalized2;
    };

    if (currentLanguage === 'en') {
      return textEn && text && !checkSimilarity(textEn, text);
    } else if (currentLanguage === 'de') {
      return textDe && text && !checkSimilarity(textDe, text);
    }

    return false;
  };

  const showTranslationButton = shouldShowTranslationButton();

  const getAuthorImage = () => {
    return (
      review?.profile_photo_url ||
      review?.author?.profileImage?.attributes?.variants?.['square-small']?.url ||
      avatarPlaceholder
    );
  };

  const getAuthorName = () => {
    const displayName = (
      review?.author_name ||
      review?.author?.attributes?.profile?.displayName ||
      ''
    ).split(' ')[0];
    return displayName.length > 8 ? displayName.slice(0, 8) + '...' : displayName;
  };

  const getRating = () => {
    return review?.rating || review?.attributes?.rating || 0;
  };

  const content = getReviewContent();
  const wordCount = content?.split(' ').length || 0;
  const shouldShowMore = wordCount > maxWordsToShow;
  const authorImage = getAuthorImage();

  return (
    <div className={css.reviewsContainer} onClick={onExpand}>
      <div className={css.reviewsContainerLeft}>
        <img
          className={css.avatarImg}
          src={getAuthorImage()}
          alt="Review author avatar"
          onError={event => {
            event.currentTarget.src = avatarPlaceholder;
          }}
        />
        <div className={css.reviewsContainerLeftText}>
          <p className={css.name}>
            {review?.profile_photo_url ? (
              <img src={googleIcon} alt="Google Icon" className={css.googleIcon} />
            ) : (
              <></>
            )}{' '}
            {getAuthorName()}
          </p>
          {(review?.rating || review?.attributes?.rating) && (
            <div className={css.singleReviewRating}>
              <IconReviewSearchPage
                averageRating={review?.rating || review?.attributes?.rating}
                showDot={false}
                textColor={null}
                decimals={1}
              />
            </div>
          )}
        </div>
      </div>
      <div className={`${css.containerRight} w-full`}>
        <p className={`${css.content} ${expanded ? css.expandedContent : css.showLines}`}>
          {content}
        </p>
        {(shouldShowMore || showTranslationButton) && (
          <div className={css.btnTextContainer}>
            <div className={css.btnText}>
              <div className={css.showMoreLessTextContainer}>
                {shouldShowMore ? (
                  <div className={css.showMoreLessText}>
                    <FormattedMessage
                      id={expanded ? 'faq.showLess' : 'EditListingOffersForm.activities.showMore'}
                    />
                    <span className={css.Gt}>{expanded ? '<' : '>'}</span>
                  </div>
                ) : <div/>}
                {showTranslationButton && (
                  <button
                    className={css.languageToggle}
                    onClick={e => {
                      e.stopPropagation();
                      setShowTranslation(!showTranslation);
                    }}
                  >
                    {currentLanguage === 'en' ? 'show original' : 'Originalsprache anzeigen'}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
